import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../store';
import api from '../../utils/api';

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
  sortOrder: number;
  createdAt: string;
}

const AdminCategories: React.FC = () => {
  const history = useHistory();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    sortOrder: 0,
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      alert('需要管理员权限');
      history.push('/');
      return;
    }
    fetchCategories();
  }, [user, history]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/categories');
      setCategories(data);
    } catch (err) {
      console.error('获取分类失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, formData);
      } else {
        await api.post('/categories', formData);
      }
      setShowModal(false);
      setEditingCategory(null);
      setFormData({ name: '', slug: '', description: '', sortOrder: 0 });
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      sortOrder: category.sortOrder,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这个分类吗？')) return;
    
    try {
      await api.delete(`/categories/${id}`);
      fetchCategories();
    } catch (err: any) {
      alert(err.response?.data?.message || '删除失败');
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\u4e00-\u9fa5]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const openAddModal = () => {
    setEditingCategory(null);
    setFormData({ name: '', slug: '', description: '', sortOrder: categories.length });
    setError('');
    setShowModal(true);
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1>分类管理</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-primary" onClick={openAddModal}>
            添加分类
          </button>
          <button className="btn btn-default" onClick={() => history.push('/admin')}>
            返回后台
          </button>
        </div>
      </div>

      {/* 分类列表 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>加载中...</div>
      ) : (
        <div className="card">
          {categories.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#999', padding: '40px' }}>
              暂无分类，请添加
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee' }}>
                  <th style={{ padding: '12px', textAlign: 'left', width: '60px' }}>ID</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>分类名称</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>别名</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>描述</th>
                  <th style={{ padding: '12px', textAlign: 'left', width: '80px' }}>排序</th>
                  <th style={{ padding: '12px', textAlign: 'left', width: '150px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px' }}>{category.id}</td>
                    <td style={{ padding: '12px', fontWeight: 'bold' }}>{category.name}</td>
                    <td style={{ padding: '12px', color: '#666' }}>{category.slug}</td>
                    <td style={{ padding: '12px', color: '#999', fontSize: '13px' }}>
                      {category.description || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>{category.sortOrder}</td>
                    <td style={{ padding: '12px' }}>
                      <button
                        onClick={() => handleEdit(category)}
                        style={{
                          padding: '4px 12px',
                          background: '#1890ff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginRight: '8px',
                        }}
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        style={{
                          padding: '4px 12px',
                          background: '#ff4d4f',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 添加/编辑弹窗 */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="card" style={{ width: '500px', maxHeight: '80vh', overflow: 'auto' }}>
            <h2 style={{ marginBottom: '20px' }}>
              {editingCategory ? '编辑分类' : '添加分类'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">分类名称 *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      name: e.target.value,
                      slug: generateSlug(e.target.value),
                    });
                  }}
                  placeholder="例如：小说、技术、历史"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">别名 *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="例如：fiction、tech、history"
                  required
                />
                <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  用于URL和API调用的唯一标识
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">描述</label>
                <textarea
                  className="form-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="分类的简要描述"
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-group">
                <label className="form-label">排序</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  min={0}
                />
                <p style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  数字越小越靠前显示
                </p>
              </div>

              {error && (
                <p style={{ color: '#ff4d4f', marginBottom: '16px' }}>{error}</p>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                  }}
                >
                  取消
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? '提交中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
